import { LinearProgress } from '@mui/material';
import type { NavigateFunction } from 'react-router-dom';
import { Link, useNavigate } from 'react-router-dom';
import { useMemo } from 'react';

import {
  Button,
  IPaginationProps,
  LinkItem,
  LoadedContent,
  NO_DATA_PLACEHOLDER,
  Page,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  useAppLayoutContextConsumer,
} from '@occc-app/design-system';
import { usePaginationParams } from '@occc-app/hooks/usePaginationParams';
import { formatMoneyAmount } from '@occc-app/lib/currency';
import { separateEveryThousandByComma } from '@occc-app/lib/numbers';
import { StoredValueProgramsQuery } from '@occc/graphql';
import { pluralize } from '@occc-app/lib/strings';
import { routeCreator as programManagementRouteCreator } from '@occc-app/routes/ProgramManagement/utils';
import { readableDate } from '@occc-app/lib/format';
import { DateTimeFormat } from '@occc-app/lib/date';

import { useGetStoredValueProgramsQuery } from '../../data-access/queries';
import { routeCreator } from '../stored-value.utils';

const PAGE_TITLE = 'Stored Value Programs';

const getStoredValueCreatePageLinks = (): LinkItem[] => [
  {
    to: programManagementRouteCreator.base(),
    label: 'Program Management',
  },
  {
    label: PAGE_TITLE,
  },
];

type StoredValueProgramListItem =
  StoredValueProgramsQuery['storedValuePrograms']['edges'][number]['node'];

const renderData =
  (loading: boolean, paginationProps: IPaginationProps, navigate: NavigateFunction) =>
  (programs: StoredValueProgramListItem[]) => {
    return (
      <>
        {loading && <LinearProgress />}
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Program Name</TableCell>
              <TableCell>Give Members</TableCell>
              <TableCell>For Every</TableCell>
              <TableCell>Available Until</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {programs.map((program) => {
              const {
                id,
                programName,
                moneyPerConversion,
                pointsPerConversion,
                discountingOffer: { expireAt },
              } = program;

              return (
                <TableRow
                  key={id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(routeCreator.update(id))}
                >
                  <TableCell>{programName}</TableCell>
                  <TableCell>
                    {formatMoneyAmount(moneyPerConversion, { fromCents: true })}
                  </TableCell>
                  <TableCell>
                    {separateEveryThousandByComma(pointsPerConversion)}&nbsp;
                    {pluralize(pointsPerConversion, 'Point')}
                  </TableCell>
                  <TableCell>
                    {expireAt
                      ? readableDate(expireAt, DateTimeFormat.DateTime12h)
                      : NO_DATA_PLACEHOLDER}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <Pagination {...paginationProps} />
      </>
    );
  };

export const StoredValueListPage = () => {
  const navigate = useNavigate();
  const links = useMemo(() => getStoredValueCreatePageLinks(), []);
  useAppLayoutContextConsumer(links);

  const [paginationInfo, onPageChange] = usePaginationParams();
  const { data, loading, error, paginationProps } = useGetStoredValueProgramsQuery({
    paginationInfo,
    onPageChange,
  });

  return (
    <Page
      browserTabTitle={PAGE_TITLE}
      pageHeaderProps={{
        title: PAGE_TITLE,
        subtitle: 'Define a mechanism by which points can be burned for stored value',
      }}
      actions={
        !loading && !data?.length ? (
          <Button
            color='secondary'
            variant='contained'
            component={Link}
            to={routeCreator.create}
            role='button'
          >
            Add Program
          </Button>
        ) : null
      }
    >
      <TableContainer>
        <TableHeader title={PAGE_TITLE} />
        <LoadedContent<StoredValueProgramListItem>
          error={error}
          loading={loading}
          items={data}
          renderData={renderData(loading, paginationProps, navigate)}
        />
      </TableContainer>
    </Page>
  );
};
